import { useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Stethoscope, ArrowLeft, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { resetPassword } from '@/services/auth';
import Button from '@/components/ui/Button';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const submitLock = useRef(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitLock.current) return;
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    submitLock.current = true;
    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. The link may have expired.');
    } finally {
      submitLock.current = false;
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] px-6">
        <div className="w-full max-w-[400px] text-center">
          <h1 className="text-[24px] font-bold text-gray-900">Invalid reset link</h1>
          <p className="mt-2 text-[14px] text-gray-500">
            This password reset link is invalid or missing. Please request a new one.
          </p>
          <Link
            to="/forgot-password"
            className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-primary-600 hover:text-primary-700"
          >
            Request new reset link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] px-6">
      <div className="w-full max-w-[400px]">
        {/* Logo */}
        <div className="mb-8 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-600">
            <Stethoscope className="h-5 w-5 text-white" />
          </div>
          <span className="text-[20px] font-bold tracking-tight text-gray-900">Cervica</span>
        </div>

        {success ? (
          /* Success state */
          <div>
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <h1 className="text-[24px] font-bold text-gray-900">Password reset!</h1>
            <p className="mt-2 text-[14px] text-gray-500">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <Button
              onClick={() => navigate('/login', { replace: true })}
              className="mt-6 w-full rounded-xl py-2.5 text-[14px]"
            >
              Sign in
            </Button>
          </div>
        ) : (
          /* Form state */
          <div>
            <h1 className="text-[24px] font-bold text-gray-900">Set new password</h1>
            <p className="mt-1 text-[14px] text-gray-500">
              Enter your new password below.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-[11px] font-bold text-red-600">!</span>
                  {error}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-[12px] font-semibold text-gray-700">
                  New password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                    placeholder="Minimum 8 characters"
                    className="block w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 pr-10 text-[14px] text-gray-900 shadow-sm transition-all placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-[12px] font-semibold text-gray-700">
                  Confirm new password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Re-enter your password"
                  className="block w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-[14px] text-gray-900 shadow-sm transition-all placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                />
              </div>

              <Button
                type="submit"
                className="w-full rounded-xl py-2.5 text-[14px]"
                isLoading={loading}
              >
                Reset password
              </Button>
            </form>

            <Link
              to="/login"
              className="mt-6 flex items-center gap-1.5 text-[13px] font-medium text-primary-600 hover:text-primary-700"
            >
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </div>
        )}

        <p className="mt-8 text-center text-[11px] text-gray-400">
          Protected by 256-bit encryption &middot; HIPAA Compliant
        </p>
      </div>
    </div>
  );
}
